import { useEffect, useState } from 'react'
import { message } from 'antd'
import dayjs from 'dayjs'
import { getOrganizationsReport, downloadOrganizationsReport } from '../../Services/Reports/OrgReportService'
import { TenantStatusEnum } from 'Common/Constants/Enums/TenantStautsEnum'

export default function useOrganizationReport() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [orderByLatest, setOrderByLatest] = useState(true)
  const [filters, setFilters] = useState({})

  const formatDateForAPI = (date) => dayjs(date).format('MM-DD-YYYY')


  
  const statusOptions = Object.entries(TenantStatusEnum).map(([key, value]) => ({
    value,
    label: key.replace(/([A-Z])/g, ' $1').trim() 
  }))


  const getStatusLabel = (statusValue) => {
    const entry = Object.entries(TenantStatusEnum).find(([key, value]) => value === statusValue)
    return entry ? entry[0].replace(/([A-Z])/g, ' $1').trim() : `Status ${statusValue}`
  }


  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case TenantStatusEnum.Pending:
        return 'orange'
      case TenantStatusEnum.Approved:
        return 'green'
      case TenantStatusEnum.ApprovedAndAdminAccessed:
        return 'blue'
      case TenantStatusEnum.Suspended:
        return 'red'
      case TenantStatusEnum.Deleted:
        return 'gray'
      default:
        return 'default'
    }
  }

  // Get status icon
  const getStatusIcon = (statusValue) => {
    switch (statusValue) {
      case TenantStatusEnum.Pending:
        return 'clock-circle'
      case TenantStatusEnum.Approved:
        return 'check-circle'
      case TenantStatusEnum.ApprovedAndAdminAccessed:
        return 'safety-certificate'
      case TenantStatusEnum.Suspended:
        return 'pause-circle'
      case TenantStatusEnum.Deleted:
        return 'delete'
      default:
        return null
    }
  }

  const fetchOrganizationReport = async () => {
    setLoading(true)
    try {
      const apiParams = {
        PageNumber: pageNumber,
        PageSize: pageSize,
      }

      // Add filters ONLY if present
      if (filters.FromDate)
        apiParams.FromDate = formatDateForAPI(filters.FromDate)

      if (filters.ToDate)
        apiParams.ToDate = formatDateForAPI(filters.ToDate)

      if (filters.Status !== undefined)
        apiParams.Status = filters.Status

      if (orderByLatest !== undefined)
        apiParams.OrderByLatest = orderByLatest

      const res = await getOrganizationsReport(apiParams)

      setData(res.data.items || [])
      setTotalCount(res.data.totalCount || 0)
    } catch (err) {
      message.error('Failed to load organization report')
    } finally {
      setLoading(false)
    }
  }


const downloadOrganizationReport = async () => {
  try {
    message.loading({ content: 'Generating PDF report...', key: 'download', duration: 0 });

    const apiParams = {
      PageNumber: pageNumber,
      PageSize: pageSize,
    };

    if (filters.FromDate) apiParams.FromDate = formatDateForAPI(filters.FromDate);
    if (filters.ToDate) apiParams.ToDate = formatDateForAPI(filters.ToDate);
    if (filters.Status !== undefined) apiParams.Status = Number(filters.Status);


    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Download timed out')), 60000) // 60 seconds timeout
    );
    
    const res = await Promise.race([
      downloadOrganizationsReport(apiParams),
      timeoutPromise
    ]);

    if (!res.data || res.data.size === 0) {
      throw new Error('Server returned empty response');
    }

    // Generate filename directly in frontend using the same pattern as backend
    const applicationName = "CoreIQPro";
    const dateStr = dayjs().format('MMM-DD-YYYY');
    const filename = `${applicationName}-Enterprises-Report-${dateStr}.pdf`;

    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    message.success({
      content: 'Organization PDF downloaded successfully!',
      key: 'download',
      duration: 3
    });

  } catch (error) {
    console.error('❌ Download Error Details:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      params: error.config?.params
    });

    // Try to read the error response if it's a blob
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        console.log('Error response text:', text.substring(0, 1000));

        if (text.startsWith('{') || text.startsWith('[')) {
          try {
            const jsonError = JSON.parse(text);
            console.log('Parsed JSON error:', jsonError);
          } catch { }
        }
      } catch { }
    }

    let errorMessage = 'Download failed';
    if (error.response?.status === 500) {
      errorMessage = 'Server error (500): Report generation service issue. Please try different parameters.';
    }

    message.error({
      content: errorMessage,
      key: 'download',
      duration: 5
    });
  }
};


  // Toggle sort order
  const toggleSortOrder = () => {
    setOrderByLatest(prev => !prev)
  }

  // Reset all filters
  const resetFilters = () => {
    setFilters({})
    setOrderByLatest(true)
    setPageNumber(1)
  }

  // Fetch data when dependencies change
  useEffect(() => {
    fetchOrganizationReport()
  }, [pageNumber, pageSize, orderByLatest, filters])

  return {
    data,
    loading,
    pageNumber,
    pageSize,
    totalCount,
    orderByLatest,
    statusOptions,
    getStatusLabel,
    getStatusColor,
    getStatusIcon,
    setPageNumber,
    setPageSize,
    setFilters,
    filters,
    setOrderByLatest,
    toggleSortOrder,
    resetFilters,
    fetchOrganizationReport,
    downloadOrganizationReport,
  }
}